import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

/**
 * GDPR Art. 15 (Right of Access) & Art. 20 (Right to Data Portability)
 * Exports all personal data associated with the authenticated user.
 * Returns JSON format for machine-readable portability.
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'GET') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  try {
    const supabaseAdmin = getSupabaseAdminClient();
    const userId = user.id;

    // Fetch profile
    const { data: profile } = await supabaseAdmin
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    // Fetch cliente record (if exists)
    const { data: cliente } = await supabaseAdmin
      .from('clientes')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    // Fetch user roles
    const { data: userRoles } = await supabaseAdmin
      .from('user_roles')
      .select('rol, created_at')
      .eq('user_id', userId);

    // Fetch documents (if cliente)
    let documentos: any[] = [];
    if (cliente) {
      const { data } = await supabaseAdmin
        .from('documentos')
        .select('id, nombre, tipo, estado, fecha_subida, procesado_ia')
        .eq('cliente_id', cliente.id)
        .order('fecha_subida', { ascending: false });
      documentos = data || [];
    }

    // Fetch incidencias
    let incidencias: any[] = [];
    if (cliente) {
      const { data } = await supabaseAdmin
        .from('incidencias')
        .select('id, estado, origen, fecha_creacion')
        .eq('cliente_id', cliente.id);
      incidencias = data || [];
    }

    // Fetch financial data
    let datosFinancieros: any[] = [];
    if (cliente) {
      const { data } = await supabaseAdmin
        .from('datos_financieros')
        .select('periodo, ingresos, gastos, resultado, margen')
        .eq('cliente_id', cliente.id);
      datosFinancieros = data || [];
    }

    // Fetch consent log
    const { data: consents } = await supabaseAdmin
      .from('gdpr_consent_log')
      .select('consent_type, granted, granted_at, revoked_at, consent_text_version')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    // Fetch notification preferences
    const { data: notifPrefs } = await supabaseAdmin
      .from('preferencias_notificaciones')
      .select('*')
      .eq('usuario_id', userId)
      .maybeSingle();

    // Fetch user preferences
    const { data: userPrefs } = await supabaseAdmin
      .from('user_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    // Fetch notification history
    const { data: notifHistory } = await supabaseAdmin
      .from('historial_notificaciones')
      .select('tipo, categoria, titulo, mensaje, enviado, leido, created_at')
      .eq('usuario_id', userId)
      .order('created_at', { ascending: false })
      .limit(100);

    // Fetch GDPR data requests history
    const { data: gdprRequests } = await supabaseAdmin
      .from('gdpr_data_requests')
      .select('request_type, status, requested_at, completed_at')
      .eq('user_id', userId);

    // Build export object
    const exportData = {
      _meta: {
        export_date: new Date().toISOString(),
        format: 'GDPR Art. 20 - Data Portability Export',
        user_id: userId,
        email: user.email,
      },
      profile: profile ? {
        nombre: profile.nombre,
        email: profile.email,
        rol: profile.rol,
        estado: profile.estado,
        telefono: profile.telefono,
        fecha_alta: profile.fecha_alta,
        ultimo_acceso: profile.ultimo_acceso,
      } : null,
      cliente: cliente ? {
        nif: cliente.nif,
        razon_social: cliente.razon_social,
        nombre_comercial: cliente.nombre_comercial,
        persona_contacto: cliente.persona_contacto,
        telefono: cliente.telefono,
        email: cliente.email,
        direccion_fiscal: cliente.direccion_fiscal,
        tipo_cliente: cliente.tipo_cliente,
        fecha_alta: cliente.fecha_alta,
        modulos_activos: {
          subida_documentos: cliente.modulo_subida_documentos,
          incidencias: cliente.modulo_incidencias,
          asistente_e2: cliente.modulo_asistente_e2,
          panel_financiero: cliente.modulo_panel_financiero,
          facturacion: cliente.modulo_facturacion,
        },
      } : null,
      roles: userRoles || [],
      documentos: documentos.map(d => ({
        id: d.id,
        nombre: d.nombre,
        tipo: d.tipo,
        estado: d.estado,
        fecha_subida: d.fecha_subida,
        procesado_ia: d.procesado_ia,
      })),
      incidencias: incidencias.map(i => ({
        id: i.id,
        estado: i.estado,
        origen: i.origen,
        fecha_creacion: i.fecha_creacion,
      })),
      datos_financieros: datosFinancieros,
      preferencias: {
        notificaciones: notifPrefs || null,
        usuario: userPrefs ? {
          modo_oscuro: userPrefs.modo_oscuro,
          idioma: userPrefs.idioma,
          tamano_pagina: userPrefs.tamano_pagina,
          vista_documentos: userPrefs.vista_documentos_por_defecto,
        } : null,
      },
      historial_notificaciones: notifHistory || [],
      consentimientos: consents || [],
      solicitudes_gdpr: gdprRequests || [],
    };

    // Log this export as a GDPR data request
    await supabaseAdmin.from('gdpr_data_requests').insert({
      user_id: userId,
      request_type: 'access',
      status: 'completed',
      completed_at: new Date().toISOString(),
      response_notes: 'Exportación automática de datos personales via API',
    });

    res.setHeader('Content-Type', 'application/json');
    res.setHeader('Content-Disposition', `attachment; filename="gdpr-export-${userId}-${Date.now()}.json"`);
    return res.status(200).json(exportData);
  } catch (error: any) {
    console.error('[GDPR Export] Error:', error);
    return res.status(500).json({ error: 'Error al exportar datos personales.' });
  }
}

export default withAuth(handler);
